import { useHead, useRoute } from "#imports";

/**
 * Composable to configure SEO meta tags for a page.
 * @param {Object} options - SEO configuration options.
 * @param {string} options.title - Page title.
 * @param {string} options.description - Page description.
 * @param {string} [options.image] - Open Graph image URL (defaults to avatar).
 * @param {string} [options.url] - Canonical URL (auto-detected if not provided).
 * @param {number} [options.imageWidth] - Image width in pixels (default: 1200).
 * @param {number} [options.imageHeight] - Image height in pixels (default: 630).
 */
export const useSEO = (options: {
  title: string;
  description: string;
  image?: string;
  url?: string;
  imageWidth?: number;
  imageHeight?: number;
}) => {
  const baseUrl = "https://diegomarmol.com";
  const route = useRoute();

  const {
    title,
    description,
    image = `${baseUrl}/android-chrome-512x512.png`,
    url = `${baseUrl}${route.fullPath}`,
    imageWidth = 512,
    imageHeight = 512,
  } = options;

  useHead({
    title,
    meta: [
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "description", content: description },

      // Open Graph
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Diego Martín Mármol" },
      { property: "og:url", content: url },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:image", content: image },
      { property: "og:image:width", content: String(imageWidth) },
      { property: "og:image:height", content: String(imageHeight) },
      { property: "og:image:alt", content: title },

      // Twitter
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image },
      { name: "twitter:image:alt", content: title },
    ],
    link: [{ rel: "canonical", href: url }],
  });
};
